import React, { ReactNode } from 'react';
import { FormattedMessageDiv, FormRadio, FormInput, FormSelect, Card, Tooltip, formatSSN, Button, Navigation } from '@odmonorepo/core-components';
import { formatDateInput, makeOptions, formatToISODate } from '@odmonorepo/odfunctions';
import { useFormContext } from 'react-hook-form';
import { Col, Row, Stack } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { Application, Person } from 'merchant-api/types';
import { useMutation } from '@tanstack/react-query';
import { patchAppDataOpts } from 'merchant-api';
import * as rfc6902 from 'rfc6902';
import { AnalyticsInstance } from 'analytics';

type ControlPersonProps = {
  children?: ReactNode;
  nextStep?: () => void;
  setIsAdditionalOwnersPassed: (value: boolean) => void;
  isAdditionalOwnersPassed: boolean;
  applicationData: Application | null | undefined;
  sessionId: string;
  placementVariant: string | null;
  analytics: AnalyticsInstance;
  fincenOpenedAt: number;
  onStepComplete: (value: boolean) => void;
};

type ControlPersonForm = { areYouControlPerson?: string; controlPerson?: Partial<Person> };

export const ControlPerson = ({
                                applicationData,
                                nextStep = () => {},
                                setIsAdditionalOwnersPassed,
                                sessionId, placementVariant,
                                analytics,
                                fincenOpenedAt,
                                onStepComplete,
                                children }: ControlPersonProps) => {
  const { watch, getValues, setValue } = useFormContext();
  const trackCompletion = () => {
    analytics.track('fincen_step_completed', { sessionId, placementVariant, substepCompleted: 'Control Person' });
    analytics.track('fincen_process_duration', { sessionId, placementVariant, durationMs: Date.now() - fincenOpenedAt });
    onStepComplete(true);
  };
  const { mutateAsync: patch } = useMutation({
    ...patchAppDataOpts(),
    onSuccess: () => {
      trackCompletion();
      nextStep();
    },
  });

  const handleConfirm = async () => {
    const values = getValues() as any;

    if (values.areYouControlPerson === 'false' && values.controlPerson) {
      const id = values.controlPerson.id ?? crypto.randomUUID();
      setValue('controlPerson.id', id);
      const persons = (applicationData?.data?.party?.persons ?? []).filter((p: any) => p.id !== id);

      const controlPerson = {
        ...values.controlPerson,
        type: 'CONTROL_PERSON',
        dateOfBirth: formatToISODate(values.controlPerson.dateOfBirth),
      };
      const output = {
        ...applicationData?.data,
        party: { ...applicationData?.data?.party, persons: [...persons, controlPerson] },
      };
      const patches = rfc6902.createPatch(applicationData?.data, output);
      await patch({ id: applicationData!.id, patches });
    } else {
      trackCompletion();
      nextStep();
    }
  };

  const showControlPersonForm = watch('areYouControlPerson') === 'false';

  return (
    <Card bodyClassName={'p-5'}>
      <Stack gap={5}>
        <Stack direction="horizontal">
          <FormattedMessageDiv className="h4 mb-0" id={'controlPerson.title'} />
          <Tooltip variant="standard" className="tooltip-container">
            <FormattedMessage id="controlPerson.tooltip" />
          </Tooltip>
        </Stack>
        <Row xs={1} md={1} className="g-3">
          <Col>
            <FormRadio name="areYouControlPerson" data-testid="areYouControlPerson" options={['true', 'false']} inline={false} />
          </Col>
        </Row>
        {showControlPersonForm && (
          <Stack gap={5}>
            <Card className="bg-light-gray border-0 shadow-none" bodyClassName="p-4">
              <Stack gap={5}>
                <FormattedMessageDiv className="h5 fw-bold" id={'controlPerson.title'} />
                <FormInput<ControlPersonForm> name={`controlPerson.firstName`} data-testid={`controlPerson-firstName`} className="bg-light" useYupErrors />
                <FormInput<ControlPersonForm> name={`controlPerson.lastName`} data-testid={`controlPerson-lastName`} className="bg-light" useYupErrors />
                <FormInput<ControlPersonForm> name={`controlPerson.addresses.0.city`} data-testid={`controlPerson-city`} className="bg-light" useYupErrors />
                <FormSelect<ControlPersonForm>
                  name={`controlPerson.addresses.0.state`}
                  data-testid={`controlPerson-state`}
                  className="bg-light"
                  options={makeOptions('business.address.state', 51)}
                  blankLabel=" "
                  useYupErrors
                />
                <FormInput<ControlPersonForm>
                  className="bg-light"
                  name={`controlPerson.dateOfBirth`}
                  data-testid={`controlPerson-dateOfBirth`}
                  formatValue={formatDateInput}
                  controlProps={{ inputMode: 'numeric' }}
                  useYupErrors
                />
                <FormInput<ControlPersonForm>
                  className="fs-mask bg-light"
                  name={`controlPerson.identifiers.0.value`}
                  data-testid={`controlPerson-ssn`}
                  stripFormatOnSave
                  useYupErrors
                  formatValue={formatSSN}
                  label={<FormattedMessageDiv id="formInput.ssn" />}
                  controlProps={{
                    inputMode: 'numeric',
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                      setTimeout(() => e.target.select(), 0);
                    },
                    onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                      (e.target as HTMLInputElement).select();
                    },
                  }}
                />
              </Stack>
            </Card>
          </Stack>
        )}
      </Stack>
      {children}
      <Navigation
        handleNext={handleConfirm}
        stacked={true}
        customBackBtn={
          <Button variant="outline-primary" onClick={() => setIsAdditionalOwnersPassed(false)}>
            <FormattedMessage id="additionalInformation.back" />
          </Button>
        }
      />
    </Card>
  );
};
